import { DiffType, GetCssClassNamesOption } from '@ardoq/data-model';
import { componentInterface } from '@ardoq/component-interface';
import { componentOperations } from '@ardoq/core';
import { GraphItem, type GraphItemMetadata } from './GraphItem';
import SparkMD5 from 'spark-md5';
import type { ItemLabels } from '../types';
import { referenceInterface } from '@ardoq/reference-interface';
import { ComponentGraphNodeModel } from './types';
import { componentHasURLFields } from './utils';

type GraphNodeConstructorArgs = {
  modelId: string;
  isGhost: boolean;
  itemLabels?: ItemLabels;
  id?: string;
};
export class GraphNode extends GraphItem implements ComponentGraphNodeModel {
  static create(
    modelId: string,
    isGhost?: boolean,
    id?: string,
    itemLabels?: ItemLabels
  ) {
    return new GraphNode({
      modelId,
      id,
      isGhost: Boolean(isGhost),
      itemLabels,
    });
  }

  public readonly isGhost: boolean;
  protected itemLabels: ItemLabels | undefined;

  private constructor({
    modelId,
    id,
    itemLabels,
    isGhost,
  }: GraphNodeConstructorArgs) {
    const graphItemMetadata: GraphItemMetadata = {
      isComponent: componentInterface.isComponent(modelId),
      isReference: referenceInterface.isReference(modelId),
      isGroup: false,
    };
    super(modelId, graphItemMetadata, id);

    this.itemLabels = itemLabels;
    this.hash = SparkMD5.hash(this.getRawHash());
    this.isGhost = isGhost;
  }
  override getVisualDiffType(): DiffType {
    const component = componentInterface.get(this.modelId);
    return component
      ? componentOperations.getVisualDiffType(component)
      : DiffType.NONE;
  }
  override getCSS(options?: GetCssClassNamesOption) {
    return componentInterface.getCssClassNames(this.modelId, options) ?? '';
  }
  override hasURLFields() {
    return componentHasURLFields(this.modelId);
  }
  override getItemLabels(): ItemLabels | undefined {
    return this.itemLabels;
  }
  override setItemLabels(itemLabels: ItemLabels): void {
    this.itemLabels = itemLabels;
  }
  override getLabel() {
    return componentInterface.getRawLabel(this.modelId) ?? '';
  }

  getNameWithFieldLabelAndValue() {
    return componentInterface.getNameWithFieldLabelAndValue(this.modelId);
  }

  getImage() {
    return componentInterface.getImage(this.modelId) ?? '';
  }

  getShape() {
    return componentInterface.getShape(this.modelId) || 'predefinedProcess';
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.getImage()}${this.getShape()}`;
  }
}
